/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useSessionStore } from '../../session/store/useSessionStore';
import { usePlanEntitlements } from '../../providers/PlanEntitlementsProvider';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { buildSupportTicketContext } from '../supportTicketContext';
import {
  insertSupportTicket,
  SUPPORT_TICKET_BODY_MAX,
  type SupportTicketType,
} from '../services/supportTicketsRepo';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { showError, showSuccess } from '../../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { ModalShell, useModalShellScroll } from '../../../shared/modals/ModalShell';
import { supportTypeLabel } from '../screens/supportPresentation';
import { SupportAttachmentPicker } from './SupportAttachmentPicker';
import { uploadSupportTicketAttachments } from '../services/supportAttachmentsRepo';
import type { FeedbackAttachmentDraft } from '@shared/feedbackAttachments';

type Props = {
  visible: boolean;
  onClose: () => void;
  onSubmitted: () => void;
};

const TYPE_OPTIONS: { id: SupportTicketType; hint: string }[] = [
        { id: 'support', hint: 'Algo no anda o necesitás una mano' },
        { id: 'improvement', hint: 'Una idea para el producto' },
];

export function SupportTicketCreateModal({ visible, onClose, onSubmitted }: Props) {
  const scrollInputIntoView = useModalShellScroll();
  const bodyInputRef = useRef<TextInput>(null);
  const session = useAuthStore((state) => state.session);
  const branches = useSessionStore((state) => state.branches);
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const currentBranch = useMemo(
    () => branches.find((b) => b.id === currentBranchId),
    [branches, currentBranchId]
  );
  const { entitlements, loading: entLoading } = usePlanEntitlements();

  const [ticketType, setTicketType] = useState<SupportTicketType>('support');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<FeedbackAttachmentDraft[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const bodyTrim = body.trim();
  const bodyTooLong = bodyTrim.length > SUPPORT_TICKET_BODY_MAX;
  const canSubmit =
    bodyTrim.length > 0 &&
    !bodyTooLong &&
    !submitting &&
    !entLoading &&
    Boolean(entitlements?.organization_id) &&
    Boolean(session?.user?.id);

  const resetForm = () => {
    setTicketType('support');
    setBody('');
    setAttachments([]);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    const orgId = entitlements?.organization_id;
    const userId = session?.user?.id;
    if (!canSubmit || !orgId || !userId) return;
    setSubmitting(true);
    try {
      const context = await buildSupportTicketContext({
        branchName: currentBranch?.name,
        branchId: currentBranchId ?? null,
      });
      const created = await insertSupportTicket({
        organizationId: orgId,
        branchId: currentBranchId ?? null,
        userId,
        ticketType,
        body: bodyTrim,
        context: context as unknown as Record<string, unknown>,
      });
      try {
        await uploadSupportTicketAttachments({ ticketId: created.id, author: 'commerce', drafts: attachments });
      } catch {
        showError('Mensaje enviado, pero faltaron los adjuntos', 'Podés volver a abrir el caso y enviarlos en un seguimiento.');
      }
      resetForm();
      showSuccess('Mensaje enviado', 'Te responderemos pronto.');
      onSubmitted();
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'shell', op: 'support_ticket.create', error: e, userMessage: 'No se pudo enviar el mensaje.' });
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <TouchableOpacity
      style={[
        styles.submitBtn,
        !canSubmit && styles.submitBtnDisabled,
      ]}
      onPress={() => void handleSubmit()}
      disabled={!canSubmit}
      accessibilityRole="button"
      accessibilityLabel="Enviar mensaje al equipo de soporte"
    >
      {submitting ? (
        <ActivityIndicator color={ticketColors.goldInk} />
      ) : (
        <Text style={styles.submitBtnText}>
          Enviar a soporte
        </Text>
      )}
    </TouchableOpacity>
  );

  return (
    <ModalShell
      visible={visible}
      onClose={handleClose}
      onRequestClose={handleClose}
      title="Escribir a soporte"
      subtitle="Contanos un problema o una idea."
      closeAccessibilityLabel="Cerrar"
      footer={footer}
      keyboardAvoiding
    >
      {!entitlements?.organization_id && !entLoading ? (
        <Text style={styles.warnText}>
          No pudimos cargar tu negocio. Reintentá o cerrá sesión.
        </Text>
      ) : null}

      <Text style={styles.sectionLabel}>Tipo</Text>
      <View style={styles.typeRow}>
        {TYPE_OPTIONS.map((opt) => {
          const selected = ticketType === opt.id;
          return (
            <TouchableOpacity
              key={opt.id}
              style={[
                styles.typeOption,
                selected && styles.typeOptionActive,
              ]}
              onPress={() => setTicketType(opt.id)}
              accessibilityRole="button"
              accessibilityState={{ selected }}
            >
              <Text style={[styles.typeOptionLabel, selected && styles.typeOptionLabelActive]}>
                {supportTypeLabel(opt.id)}
              </Text>
              <Text style={styles.typeOptionHint}>{opt.hint}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <View style={styles.divider} />

      <Text style={styles.sectionLabel}>Descripción</Text>
      <TextInput
        ref={bodyInputRef}
        style={[
          styles.textInput,
          styles.textArea,
          bodyTooLong && styles.textAreaError,
        ]}
        multiline
        textAlignVertical="top"
        placeholder="Qué pasó, qué esperabas y en qué pantalla…"
        placeholderTextColor={ticketColors.ink3}
        value={body}
        onChangeText={setBody}
        maxLength={SUPPORT_TICKET_BODY_MAX}
        editable={!submitting}
        accessibilityLabel="Descripción del reporte"
        onFocus={() => scrollInputIntoView(bodyInputRef)}
      />
      <Text style={styles.charCount}>
        {bodyTrim.length}/{SUPPORT_TICKET_BODY_MAX.toLocaleString('es-AR')}
      </Text>
      <SupportAttachmentPicker value={attachments} onChange={setAttachments} disabled={submitting} />
      {bodyTooLong ? (
        <Text style={styles.limitWarn}>Acortá el mensaje para poder enviarlo.</Text>
      ) : null}
    </ModalShell>
  );
}

const styles = StyleSheet.create({
  warnText: {
    fontSize: 13,
    color: '#B45309',
    fontWeight: '500',
    lineHeight: 18,
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: ticketColors.ruleSoft,
    marginVertical: spacing.sm,
  },
  typeRow: {
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  typeOption: {
    alignSelf: 'stretch',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: '#FAFBFA',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    gap: 2,
  },
  typeOptionActive: {
    borderColor: ticketColors.gold,
    backgroundColor: '#E8F7F4',
  },
  typeOptionLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  typeOptionLabelActive: {
    color: ticketColors.ink,
  },
  typeOptionHint: {
    fontSize: 13,
    color: ticketColors.ink3,
    lineHeight: 18,
  },
  textInput: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: Platform.OS === 'ios' ? 12 : 10,
    color: ticketColors.ink,
    backgroundColor: '#FAFBFA',
    fontSize: 15,
    fontWeight: '400',
    marginTop: spacing.xs,
  },
  textArea: {
    minHeight: 120,
    textAlignVertical: 'top',
  },
  textAreaError: {
    borderColor: '#B45309',
  },
  charCount: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontWeight: '500',
    marginTop: 4,
    textAlign: 'right',
  },
  limitWarn: {
    fontSize: 12,
    color: '#B45309',
    fontWeight: '500',
  },
  submitBtn: {
    flexGrow: 0,
    flexBasis: 'auto',
    alignSelf: 'stretch',
    minHeight: 48,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
    paddingVertical: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitBtnDisabled: {
    opacity: 0.5,
  },
  submitBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});
